/**
 * Definição dos planos e add-ons EventPro v2.
 * IMPORTANTE: o frontend usa isso pra exibir; a fonte da verdade é
 * `system_plans/{id}` no Firestore (gerenciada pelo master).
 */
import type { PlanId, Permission, Role } from '@/types'

export interface PlanDef {
  id: PlanId
  nome: string
  preco_mes: number
  features: string[]
  limites: {
    clients_max: number          // -1 = ilimitado
    events_per_month_max: number
    team_max: number
    suppliers_max: number
    storage_mb: number
    users_max: number
  }
  sla_horas: number              // tempo máximo de resposta de suporte
}

export const PLANS: Record<PlanId, PlanDef> = {
  basico: {
    id: 'basico',
    nome: 'Básico',
    preco_mes: 197,
    features: [
      'dashboard',
      'clients',
      'events',
      'proposals_simple',
      'finance_basic',
      'support',
    ],
    limites: {
      clients_max: 50,
      events_per_month_max: 30,
      team_max: 5,
      suppliers_max: 10,
      storage_mb: 100,
      users_max: 1,
    },
    sla_horas: 48,
  },
  intermediario: {
    id: 'intermediario',
    nome: 'Intermediário',
    preco_mes: 397,
    features: [
      'dashboard',
      'clients',
      'events',
      'proposals_simple',
      'proposals_pdf',
      'finance_basic',
      'finance_advanced',
      'suppliers',
      'team',
      'reports',
      'budget_simulator',
      'support',
      'contracts',
      'contracts_pdf',
      'whatsapp_templates',
    ],
    limites: {
      clients_max: 300,
      events_per_month_max: 150,
      team_max: 25,
      suppliers_max: 50,
      storage_mb: 1024,
      users_max: 3,
    },
    sla_horas: 24,
  },
  completo: {
    id: 'completo',
    nome: 'Completo',
    preco_mes: 697,
    features: [
      'dashboard',
      'clients',
      'events',
      'proposals_simple',
      'proposals_pdf',
      'finance_basic',
      'finance_advanced',
      'suppliers',
      'team',
      'reports',
      'reports_premium',
      'budget_simulator',
      'support',
      'contracts',
      'contracts_pdf',
      'whatsapp_templates',
      'whatsapp_api',
      'whatsapp_zapi',
      'leads_bot',
      'formaturas',
      'import_csv',
      'backup_auto',
    ],
    limites: {
      clients_max: -1,
      events_per_month_max: -1,
      team_max: -1,
      suppliers_max: -1,
      storage_mb: 5120,
      users_max: 10,
    },
    sla_horas: 4,
  },
}

export interface AddonDef {
  id: string
  nome: string
  preco_mes: number
  feature: string
  /** Cota mensal (quando relevante). Ex: nº de conversas IA por mês. */
  cota_mensal?: number
  /** Add-ons que precisam estar ativos pra este funcionar.
   *  Ex: IA WhatsApp depende de WhatsApp Inbox. */
  requer?: string[]
  /** Descrição curta pra exibição na UI/landing. */
  descricao?: string
}

export const ADDONS: AddonDef[] = [
  // ===== WhatsApp =====
  {
    id: 'whatsapp_inbox',
    nome: 'WhatsApp Inbox',
    preco_mes: 100,
    feature: 'whatsapp_inbox',
    descricao: 'Painel multi-atendente integrado ao seu WhatsApp via Z-API',
  },

  // ===== IA WhatsApp (3 níveis — depende do WhatsApp Inbox) =====
  {
    id: 'ia_whatsapp_essencial',
    nome: 'IA WhatsApp · Essencial',
    preco_mes: 249,
    feature: 'ia_whatsapp',
    cota_mensal: 100,
    requer: ['whatsapp_inbox'],
    descricao: 'IA copiloto Claude responde até 100 conversas/mês. Filtra leads e libera você pra atender só os prontos.',
  },
  {
    id: 'ia_whatsapp_profissional',
    nome: 'IA WhatsApp · Profissional',
    preco_mes: 449,
    feature: 'ia_whatsapp',
    cota_mensal: 300,
    requer: ['whatsapp_inbox'],
    descricao: 'Até 300 conversas/mês + análise mensal de desempenho + suporte WhatsApp prioritário.',
  },
  {
    id: 'ia_whatsapp_premium',
    nome: 'IA WhatsApp · Premium',
    preco_mes: 797,
    feature: 'ia_whatsapp',
    requer: ['whatsapp_inbox'],
    descricao: 'Conversas ilimitadas com monitor de qualidade. IA treinada nos seus contratos e propostas.',
  },

  // ===== Demais add-ons =====
  { id: 'leads_bot', nome: 'Bot de Leads', preco_mes: 99, feature: 'leads_bot' },
  { id: 'reports_premium', nome: 'Relatórios Premium', preco_mes: 49, feature: 'reports_premium' },
  { id: 'contracts_advanced', nome: 'Contratos Avançados', preco_mes: 39, feature: 'contracts_advanced' },
  { id: 'backup_auto', nome: 'Backup Automático', preco_mes: 19, feature: 'backup_auto' },
  { id: 'extra_user', nome: 'Usuário Extra', preco_mes: 29, feature: 'extra_user' },
  { id: 'extra_storage', nome: 'Armazenamento Extra (+5GB)', preco_mes: 19, feature: 'extra_storage' },
]

/** Devolve a cota IA (conversas/mês) do tenant baseada nos add-ons ativos.
 *  -1 = ilimitado (Premium). 0 = não tem IA. */
export function getIACotaMensal(addons: string[]): number {
  if (addons.includes('ia_whatsapp_premium')) return -1
  if (addons.includes('ia_whatsapp_profissional')) return 300
  if (addons.includes('ia_whatsapp_essencial')) return 100
  return 0
}

/** Valida se um add-on tem todas as dependências satisfeitas.
 *  Retorna lista de add-ons faltantes (vazio = ok). */
export function validarDependenciasAddon(addonId: string, addonsAtivos: string[]): string[] {
  const def = ADDONS.find(a => a.id === addonId)
  if (!def?.requer) return []
  return def.requer.filter(req => !addonsAtivos.includes(req))
}

// =========================== ROLES → PERMISSIONS ===========================

const ALL_READ: Permission[] = [
  'clients.read', 'events.read', 'proposals.read', 'contracts.read',
  'finance.read', 'suppliers.read', 'team.read', 'reports.read',
  'whatsapp.read', 'leads.read', 'settings.read', 'support.read',
]

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  master: [
    // master tem tudo; checagens usam role === 'master' direto.
  ],
  tenant_owner: [
    'clients.read', 'clients.write', 'clients.delete',
    'events.read', 'events.write', 'events.delete',
    'proposals.read', 'proposals.write', 'proposals.send',
    'contracts.read', 'contracts.write', 'contracts.generate_pdf',
    'finance.read', 'finance.write', 'finance.export',
    'suppliers.read', 'suppliers.write',
    'team.read', 'team.write',
    'reports.read', 'reports.export_premium',
    'whatsapp.read', 'whatsapp.send', 'whatsapp.configure',
    'leads.read', 'leads.write',
    'settings.read', 'settings.write',
    'billing.read', 'billing.write',
    'team_management.invite', 'team_management.remove',
    'support.read', 'support.write',
  ],
  tenant_admin: [
    'clients.read', 'clients.write', 'clients.delete',
    'events.read', 'events.write', 'events.delete',
    'proposals.read', 'proposals.write', 'proposals.send',
    'contracts.read', 'contracts.write', 'contracts.generate_pdf',
    'finance.read', 'finance.write', 'finance.export',
    'suppliers.read', 'suppliers.write',
    'team.read', 'team.write',
    'reports.read',
    'whatsapp.read', 'whatsapp.send',
    'leads.read', 'leads.write',
    'settings.read',
    'support.read', 'support.write',
  ],
  tenant_finance: [
    ...ALL_READ,
    'finance.write', 'finance.export',
  ],
  tenant_team: [
    'clients.read', 'clients.write',
    'events.read', 'events.write',
    'proposals.read',
    'suppliers.read', 'suppliers.write',
    'team.read',
    'whatsapp.read', 'whatsapp.send',
    'leads.read', 'leads.write',
    'support.read', 'support.write',
  ],
  tenant_viewer: ALL_READ,
}

// =========================== HELPERS ===========================

export function planAllows(planId: PlanId, feature: string): boolean {
  return PLANS[planId].features.includes(feature)
}

export function planLimit(planId: PlanId, key: keyof PlanDef['limites']): number {
  return PLANS[planId].limites[key] ?? -1
}

export function tenantHasFeature(planId: PlanId, addons: string[], feature: string): boolean {
  return planAllows(planId, feature) || addons.includes(feature)
}
